import React, { useState, useEffect } from 'react';
import styled, { createGlobalStyle, keyframes } from 'styled-components';
import ko from '../language/Ko-kr.json';
import en from '../language/En-eng.json';
import { ResponsiveShell } from '../components/ResponsiveShell';

const isKorean = true; // TODO: 실제 언어 설정 연동
const t = isKorean ? ko : en;

/* ─── Global ─────────────────────────────────────────────────────────────── */
const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Jua&family=Noto+Sans+KR:wght@400;500;700&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { height: 100%; overflow: hidden; }
  body {
    font-family: 'Noto Sans KR', sans-serif;
    background: #E8E0C8;
    color: #5C5040;
    -webkit-font-smoothing: antialiased;
    height: 100dvh;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  #root {
    width: 100%;
    height: 100dvh;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
  }
`;

/* ─── Animations ─────────────────────────────────────────────────────────── */
const slideUp = keyframes`
  from { transform: translateY(100%); opacity: 0.5; }
  to { transform: translateY(0); opacity: 1; }
`;

/* ─── Phone Shell ────────────────────────────────────────────────────────── */

/* ─── Nav Bar ────────────────────────────────────────────────────────────── */
const PhoneNavBar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 20px 12px;
  flex-shrink: 0;
  background: rgba(249, 246, 232, 0.95);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1.5px solid #EDE9D5;
  z-index: 10;
`;

const BackBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: #EDE9D5;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.2s, transform 0.15s;
  color: #5C5040;
  &:hover { background: #E0D8C0; transform: scale(1.05); }
  &:active { transform: scale(0.95); }
`;

const NavTitle = styled.h2`
  font-family: 'Jua', sans-serif;
  font-size: 18px;
  color: #3D3020;
  flex: 1;
  text-align: center;
  margin-right: 36px; /* offset for back button */
`;

/* ─── Body ───────────────────────────────────────────────────────────────── */
const PhoneBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 32px 24px;
  gap: 24px;
`;

const InfoCard = styled.div`
  background: #FFF;
  border: 1.5px solid #D8D0B8;
  border-radius: 20px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: 0 4px 12px rgba(90, 60, 30, 0.05);
`;

const InfoRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const InfoLabel = styled.div`
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 12px;
  color: #B0A080;
  font-weight: 700;
`;

const InfoValue = styled.div`
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 16px;
  color: #3D3020;
  font-weight: 500;
`;

const DangerZone = styled.div`
  margin-top: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  align-items: center;
`;

const DeleteBtn = styled.button`
  background: #FFF;
  color: #EA4335;
  border: 1.5px solid #FAD2CF;
  border-radius: 16px;
  padding: 16px;
  width: 100%;
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #FCE8E6;
    border-color: #EA4335;
  }
  &:active {
    transform: scale(0.98);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const DangerText = styled.p`
  font-size: 12px;
  color: #B0A080;
  text-align: center;
  line-height: 1.5;
`;

const ToggleSwitch = styled.label`
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  input { opacity: 0; width: 0; height: 0; }
  span {
    position: absolute; cursor: pointer;
    top: 0; left: 0; right: 0; bottom: 0;
    background-color: #D8D0B8;
    transition: .4s;
    border-radius: 24px;
  }
  span:before {
    position: absolute; content: "";
    height: 18px; width: 18px;
    left: 3px; bottom: 3px;
    background-color: white;
    transition: .4s;
    border-radius: 50%;
  }
  input:checked + span {
    background-color: #E07040;
  }
  input:checked + span:before {
    transform: translateX(20px);
  }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1.5px solid #F0ECE1;
  &:last-child { border-bottom: none; }
`;

const ToggleLabel = styled.div`
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 15px;
  color: #3D3020;
  font-weight: 500;
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 15px;
  color: #3D3020;
  font-weight: 500;
  cursor: pointer;
  border-bottom: 1.5px solid #F0ECE1;
  &:last-child { border-bottom: none; }
  &:hover { opacity: 0.8; }
`;

/* ─── Modal ──────────────────────────────────────────────────────────────── */
const ModalOverlay = styled.div`
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
`;

const ModalContent = styled.div`
  background: #F9F6E8;
  padding: 24px;
  border-radius: 20px;
  width: 80%;
  max-width: 320px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-shadow: 0 12px 32px rgba(0,0,0,0.2);
`;

const ModalTitle = styled.h3`
  font-family: 'Jua', sans-serif;
  font-size: 18px;
  color: #EA4335;
  text-align: center;
`;

const ModalDesc = styled.p`
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 13px;
  color: #5C5040;
  text-align: center;
  line-height: 1.4;
`;

const ModalInput = styled.input`
  width: 100%;
  border: 1.5px solid #D8D0B8;
  border-radius: 12px;
  padding: 12px 16px;
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 14px;
  outline: none;
  
  &:focus {
    border-color: #EA4335;
  }
`;

const ModalBtnGroup = styled.div`
  display: flex;
  gap: 12px;
`;

const ModalBtn = styled.button<{ $danger?: boolean }>`
  flex: 1;
  padding: 12px;
  border-radius: 12px;
  border: none;
  font-family: 'Noto Sans KR', sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  background: ${p => p.$danger ? '#EA4335' : '#EDE9D5'};
  color: ${p => p.$danger ? '#FFF' : '#5C5040'};
`;

/* ─── Component ──────────────────────────────────────────────────────────── */
const Setting: React.FC = () => {
    const [userInfo, setUserInfo] = useState<{ user_email: string; user_name: string; user_alias: string } | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [deletePassword, setDeletePassword] = useState('');
    const [isNotificationEnabled, setIsNotificationEnabled] = useState(false);
    const [popup, setPopup] = useState<{ title: string; msg: string; onClose?: () => void } | null>(null);
    const showPopup = (title: string, msg: string, onClose?: () => void) => setPopup({ title, msg, onClose });

    useEffect(() => {
        if ('Notification' in window) {
            setIsNotificationEnabled(Notification.permission === 'granted');
        }
    }, []);

    const handleNotificationToggle = async () => {
        if (!('Notification' in window)) {
            showPopup('알림 설정', '이 브라우저는 알림 기능을 지원하지 않습니다.');
            return;
        }
        if (isNotificationEnabled) {
            showPopup('알림 설정', '알림 권한은 브라우저 설정에서 직접 해제하셔야 합니다.');
        } else {
            Notification.requestPermission().then((permission) => {
                if (permission === 'granted') {
                    setIsNotificationEnabled(true);
                    new Notification('알림 설정 완료', { body: '성공적으로 알림 권한이 허용되었습니다!' });
                } else {
                    showPopup('알림 설정', '알림 권한이 차단되어 있습니다. 브라우저 설정에서 권한을 직접 허용해주세요.');
                }
            });
        }
    };

    useEffect(() => {
        // 사용자 정보 불러오기
        const fetchUserInfo = async () => {
            try {
                const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
                if (!token) {
                    showPopup('설정 안내', t.setting.loginRequired, () => { window.location.href = '/start'; });
                    return;
                }

                const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/user/me`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }).catch(() => null);

                if (response && response.ok) {
                    const data = await response.json();
                    setUserInfo(data);
                } else {
                    console.warn("API '/user/me' failed or DB is not ready yet.");
                    setUserInfo(null);
                }
            } catch (err) {
                console.error(err);
                setUserInfo(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchUserInfo();
    }, []);

    const handleDeleteAccount = async () => {
        if (!deletePassword.trim()) {
            showPopup('계정 탈퇴', t.setting.passwordRequired);
            return;
        }

        setIsDeleting(true);
        try {
            const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
            if (!token) {
                showPopup('계정 탈퇴', t.setting.noLoginInfo, () => { window.location.href = '/start'; });
                return;
            }
            const response = await fetch(`${process.env.REACT_APP_API_URL || ''}/user/me`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ password: deletePassword })
            });

            if (response.ok || response.status === 404) {
                showPopup('계정 탈퇴', t.setting.deleteSuccess, () => {
                    localStorage.clear();
                    sessionStorage.clear();
                    window.location.href = '/start';
                });
            } else {
                showPopup('계정 탈퇴', t.setting.deleteFail);
            }
        } catch (err) {
            console.error(err);
            showPopup('계정 탈퇴', t.setting.deleteError);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <>
            <GlobalStyle />
            <ResponsiveShell>
                <PhoneNavBar>
                    <BackBtn onClick={() => { window.location.href = '/'; }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="15 18 9 12 15 6" />
                        </svg>
                    </BackBtn>
                    <NavTitle>{t.setting.title}</NavTitle>
                </PhoneNavBar>

                <PhoneBody>
                    <InfoCard>
                        {isLoading ? (
                            <InfoValue style={{ textAlign: 'center', padding: '20px' }}>{t.setting.loading}</InfoValue>
                        ) : userInfo ? (
                            <>
                                <InfoRow>
                                    <InfoLabel>{t.setting.emailLabel}</InfoLabel>
                                    <InfoValue>{userInfo.user_email || t.setting.unknown}</InfoValue>
                                </InfoRow>
                                <InfoRow>
                                    <InfoLabel>{t.setting.nicknameLabel}</InfoLabel>
                                    <InfoValue>{userInfo.user_name || t.setting.unknown}</InfoValue>
                                </InfoRow>
                                <InfoRow>
                                    <InfoLabel>{t.setting.idTagLabel}</InfoLabel>
                                    <InfoValue>{userInfo.user_alias || '@unknown'}</InfoValue>
                                </InfoRow>
                            </>
                        ) : (
                            <InfoValue style={{ textAlign: 'center', padding: '20px', color: '#EA4335' }}>
                                {t.setting.loadFail}
                            </InfoValue>
                        )}
                    </InfoCard>

                    <InfoCard style={{ padding: '8px 24px' }}>
                        <ToggleRow>
                            <ToggleLabel>{isKorean ? '알림 허용' : 'Notifications'}</ToggleLabel>
                            <ToggleSwitch>
                                <input type="checkbox" checked={isNotificationEnabled} onChange={handleNotificationToggle} />
                                <span></span>
                            </ToggleSwitch>
                        </ToggleRow>
                    </InfoCard>

                    <DangerZone>
                        <DangerText>
                            {t.setting.deleteDesc}
                        </DangerText>
                        <DeleteBtn onClick={() => { setIsModalOpen(true); setDeletePassword(''); }} disabled={isLoading}>
                            {t.setting.deleteBtn}
                        </DeleteBtn>
                    </DangerZone>
                </PhoneBody>

                {isModalOpen && (
                    <ModalOverlay>
                        <ModalContent>
                            <ModalTitle>{t.setting.deleteBtn}</ModalTitle>
                            <ModalDesc>{t.setting.deleteModalDesc}</ModalDesc>
                            <ModalInput 
                                type="password"
                                placeholder={t.setting.deletePasswordPlaceholder}
                                value={deletePassword}
                                onChange={(e) => setDeletePassword(e.target.value)}
                            />
                            <ModalBtnGroup>
                                <ModalBtn onClick={() => { setIsModalOpen(false); setDeletePassword(''); }}>
                                    {t.setting.cancel}
                                </ModalBtn>
                                <ModalBtn $danger onClick={handleDeleteAccount} disabled={isDeleting}>
                                    {isDeleting ? t.setting.deletingBtn : t.setting.deleteConfirmBtn}
                                </ModalBtn>
                            </ModalBtnGroup>
                        </ModalContent>
                    </ModalOverlay>
                )}

                {/* ── 인라인 알림 팝업 ── */}
                {popup && (
                  <div style={{
                    position: 'fixed', inset: 0,
                    background: 'rgba(40,30,20,0.55)',
                    backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)',
                    zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '20px', boxSizing: 'border-box'
                  }}>
                    <div style={{
                      background: '#FDFBF4', borderRadius: '20px', padding: '28px 24px 20px',
                      width: '90%', maxWidth: '340px', boxShadow: '0 16px 40px rgba(40,30,20,0.3)',
                      display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center'
                    }}>
                      <div style={{
                        width: 48, height: 48, borderRadius: '50%', background: '#FFF0EA',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        marginBottom: 14, color: '#E07040'
                      }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                        </svg>
                      </div>
                      <p style={{ fontWeight: 700, fontSize: 17, color: '#3D3020', marginBottom: 8 }}>{popup.title}</p>
                      <p style={{ fontSize: 14, color: '#6C5D47', lineHeight: 1.6, marginBottom: 22, whiteSpace: 'pre-line' }}>{popup.msg}</p>
                      <button
                        onClick={() => { const cb = popup.onClose; setPopup(null); cb?.(); }}
                        style={{
                          width: '100%', padding: '13px', borderRadius: 12, border: 'none', cursor: 'pointer',
                          background: 'linear-gradient(135deg,#E07040,#D05828)', color: '#fff',
                          fontSize: 15, fontWeight: 700, boxShadow: '0 4px 14px rgba(224,112,64,0.3)'
                        }}
                      >확인</button>
                    </div>
                  </div>
                )}
            </ResponsiveShell>
        </>
    );
};

export default Setting;
